import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, Check, CheckCheck, MessageSquare, UserPlus, UserCheck, UserX, FileCheck, FileX, Megaphone, X, ChevronRight, PackageCheck, PackageX } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NOTIF_ICONS = {
  collab_invite: { icon: UserPlus, color: '#3B82F6', bg: '#EFF6FF' },
  collab_accepted: { icon: UserCheck, color: '#10B981', bg: '#ECFDF5' },
  collab_declined: { icon: UserX, color: '#EF4444', bg: '#FEF2F2' },
  collab_ended: { icon: X, color: '#6B7280', bg: '#F3F4F6' },
  content_submitted: { icon: FileCheck, color: '#8B5CF6', bg: '#F5F3FF' },
  content_approved: { icon: Check, color: '#10B981', bg: '#ECFDF5' },
  content_rejected: { icon: FileX, color: '#EF4444', bg: '#FEF2F2' },
  campaign_application: { icon: Megaphone, color: '#F59E0B', bg: '#FFFBEB' },
  campaign_accepted: { icon: PackageCheck, color: '#10B981', bg: '#ECFDF5' },
  campaign_rejected: { icon: PackageX, color: '#EF4444', bg: '#FEF2F2' },
  campaign_content_approved: { icon: Check, color: '#10B981', bg: '#ECFDF5' },
  campaign_content_rejected: { icon: FileX, color: '#EF4444', bg: '#FEF2F2' },
  campaign_content_submitted: { icon: FileCheck, color: '#8B5CF6', bg: '#F5F3FF' },
  chat_message: { icon: MessageSquare, color: '#3B82F6', bg: '#EFF6FF' },
  default: { icon: Bell, color: '#6B7280', bg: '#F3F4F6' }
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function NotificationInbox() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/notifications/inbox?page=${pageNum}&limit=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setNotifications(prev => append ? [...prev, ...data.notifications] : data.notifications);
      setUnreadCount(data.unread_count);
      setHasMore(data.has_more);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch notifications inbox:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch on mount and periodically
  useEffect(() => {
    if (!token) return;
    fetchNotifications();
    const interval = setInterval(() => fetchNotifications(), 10000);
    return () => clearInterval(interval);
  }, [token, fetchNotifications]);

  // Refetch when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        await fetch(`${API}/api/notifications/inbox/${notif.id}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark notification read:', err);
      }
    }
    setOpen(false);
    // Navigate to the target page
    if (notif.url) navigate(notif.url);
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API}/api/notifications/inbox/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all read:', err);
    }
  };

  const getIcon = (type) => {
    const config = NOTIF_ICONS[type] || NOTIF_ICONS.default;
    const Icon = config.icon;
    return (
      <div
        className="flex items-center justify-center rounded-full shrink-0"
        style={{ width: 36, height: 36, background: config.bg }}
      >
        <Icon size={18} style={{ color: config.color }} />
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        data-testid="notification-bell-btn"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={22} className="text-gray-600" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full bg-rose-500 text-white font-semibold"
            style={{ minWidth: 18, height: 18, fontSize: 11, padding: '0 4px' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel - fixed on mobile, absolute on desktop */}
      {open && (
        <>
          {/* Mobile overlay backdrop */}
          <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={() => setOpen(false)} />
          
          <div
            data-testid="notification-inbox-dropdown"
            className="fixed inset-x-2 top-16 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
            style={{ maxWidth: 'min(380px, calc(100vw - 16px))', maxHeight: 'min(480px, calc(100vh - 100px))' }}
          >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  data-testid="mark-all-read-btn"
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-xs text-rose-500 hover:text-rose-700 font-medium transition-colors"
                >
                  <CheckCheck size={14} /> Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto" style={{ maxHeight: 370 }}>
            {notifications.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-gray-400">
                <Bell size={32} className="mb-2 opacity-40" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <>
                {notifications.map((notif) => (
                  <button
                    key={notif.id}
                    data-testid={`notification-item-${notif.id}`}
                    onClick={() => handleNotificationClick(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 ${
                      !notif.read ? 'bg-rose-50/40' : ''
                    }`}
                  >
                    {getIcon(notif.type)}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.body}</p>
                      <p className="text-xs text-gray-400 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full bg-rose-500 shrink-0 mt-2" />
                    )}
                  </button>
                ))}
                {hasMore && (
                  <button
                    onClick={() => fetchNotifications(page + 1, true)}
                    className="w-full py-2.5 text-sm text-rose-500 hover:text-rose-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    {loading ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100">
            <button
              data-testid="see-all-notifications-btn"
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="w-full flex items-center justify-center gap-1 py-2.5 text-sm text-rose-500 hover:text-rose-700 font-medium hover:bg-gray-50 transition-colors"
            >
              See all notifications <ChevronRight size={14} />
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
